import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_Reference() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">
        📘 CSS Reference: Complete Property Guide
      </h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The CSS Reference provides a categorized overview of all essential CSS
        properties. It’s a quick lookup to understand how to style layouts,
        typography, backgrounds, borders, and more. Ideal for both beginners and
        professionals looking to recall key syntax and usage.
      </p>

      {/* 🔑 Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#1e3a8a" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            CSS stands for <strong>Cascading Style Sheets</strong> — used to
            style and layout HTML elements.
          </li>
          <li>
            Properties are grouped by purpose: layout, typography, color,
            backgrounds, borders, etc.
          </li>
          <li>
            Each property includes a <code>name</code> and <code>value</code>,
            separated by a colon, and ends with a semicolon.
          </li>
          <li>
            CSS can be written inline, internally (inside
            <code>&lt;style&gt;</code>), or externally in <code>.css</code>{" "}
            files.
          </li>
          <li>
            Use <code>selectors</code> to target specific HTML elements or
            groups.
          </li>
          <li>
            Modern CSS supports responsive design, animations, and variables for
            reusable themes.
          </li>
        </ul>
      </div>

      {/* Layout Properties */}
      <h2 className="section-title">🧱 Layout & Display</h2>
      <CodeCopyBlock
        code={`display: block | inline | inline-block | flex | grid | none;
position: static | relative | absolute | fixed | sticky;
top / right / bottom / left: <length> | <percentage>;
z-index: <number>;
overflow: visible | hidden | scroll | auto;
float: left | right | none;
clear: left | right | both;
`}
      />

      {/* Box Model */}
      <h2 className="section-title">📦 Box Model</h2>
      <CodeCopyBlock
        code={`margin: <length> | auto;
padding: <length>;
border: <width> <style> <color>;
border-radius: <length>;
box-sizing: content-box | border-box;
width / height / min-width / max-width: <length> | <percentage>;
`}
      />

      {/* Typography */}
      <h2 className="section-title">🔤 Typography</h2>
      <CodeCopyBlock
        code={`font-family: "Arial", sans-serif;
font-size: <size>;
font-weight: normal | bold | bolder | lighter | <100-900>;
font-style: normal | italic | oblique;
text-align: left | center | right | justify;
text-decoration: none | underline | line-through;
line-height: <number> | <length>;
letter-spacing: <length>;
word-spacing: <length>;
color: <color>;
`}
      />

      {/* Background */}
      <h2 className="section-title">🌄 Background</h2>
      <CodeCopyBlock
        code={`background-color: <color>;
background-image: url("image.jpg");
background-repeat: no-repeat | repeat | repeat-x | repeat-y;
background-position: center | top | left | right | bottom | <x> <y>;
background-size: auto | cover | contain;
background-attachment: scroll | fixed | local;
`}
      />

      {/* Border & Outline */}
      <h2 className="section-title">🧩 Borders & Outline</h2>
      <CodeCopyBlock
        code={`border: 2px solid #333;
border-top / border-right / border-bottom / border-left: <style>;
border-radius: 8px;
outline: 2px solid red;
outline-offset: 4px;
box-shadow: 2px 4px 8px rgba(0,0,0,0.3);
`}
      />

      {/* Flexbox */}
      <h2 className="section-title">🧭 Flexbox</h2>
      <CodeCopyBlock
        code={`display: flex;
flex-direction: row | column;
flex-wrap: nowrap | wrap;
justify-content: flex-start | center | space-between;
align-items: flex-start | center | stretch;
align-content: center | space-between;
gap: <length>;
flex: <grow> <shrink> <basis>;
order: <number>;
`}
      />

      {/* Grid */}
      <h2 className="section-title">🔲 CSS Grid</h2>
      <CodeCopyBlock
        code={`display: grid;
grid-template-columns: <track-list>;
grid-template-rows: <track-list>;
grid-gap: <length>;
grid-column / grid-row: <start> / <end>;
justify-items / align-items: start | center | end | stretch;
justify-content / align-content: start | center | end | space-between;
`}
      />

      {/* Transitions & Animations */}
      <h2 className="section-title">🎞️ Transitions & Animations</h2>
      <CodeCopyBlock
        code={`transition: property duration timing-function delay;
@keyframes slideIn {
  from { transform: translateX(-100px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}
animation: slideIn 1s ease-out forwards;
`}
      />

      {/* Transform & Filter */}
      <h2 className="section-title">🎨 Transform & Filter</h2>
      <CodeCopyBlock
        code={`transform: translate(x, y) rotate(45deg) scale(1.2);
transform-origin: center;
filter: blur(5px) brightness(120%) contrast(90%) grayscale(50%);
`}
      />

      {/* List & Table */}
      <h2 className="section-title">📋 Lists & Tables</h2>
      <CodeCopyBlock
        code={`list-style: disc | circle | square | none;
list-style-position: inside | outside;
border-collapse: collapse | separate;
border-spacing: <length>;
table-layout: auto | fixed;
`}
      />

      {/* Others */}
      <h2 className="section-title">⚙️ Miscellaneous</h2>
      <CodeCopyBlock
        code={`cursor: pointer | default | move | wait;
opacity: <0–1>;
visibility: visible | hidden | collapse;
clip-path: circle() | polygon() | inset();
object-fit: fill | contain | cover | none | scale-down;
`}
      />

      {/* 💡 Tips Section */}
      <div
        style={{
          background: "linear-gradient(135deg, #dcfce7, #bbf7d0)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#166534" }}>💡 Tips</h2>
        <ul className="tutorial-list-points">
          <li>Use developer tools to inspect applied CSS live.</li>
          <li>
            Combine logical properties like <code>margin-inline</code> and{" "}
            <code>padding-block</code> for better responsiveness.
          </li>
          <li>
            Use <code>var(--variable)</code> with <code>:root</code> for global
            theming.
          </li>
          <li>
            Learn advanced selectors (<code>:nth-child</code>,{" "}
            <code>:not()</code>, <code>::before</code>) for clean styling.
          </li>
        </ul>
      </div>

      {/* 🚀 Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Explore <strong>CSS Grid</strong> and <strong>Flexbox</strong> for
            advanced layout techniques.
          </li>
          <li>
            Learn about <code>@media</code> queries for responsive design.
          </li>
          <li>
            Study CSS variables and custom properties for consistent theming.
          </li>
          <li>
            Combine transitions and animations for smooth interactive effects.
          </li>
          <li>
            Move on to practical projects like cards, galleries, and navigation
            bars.
          </li>
        </ul>
      </div>
    </div>
  );
}
